// Portal Insights — the admin screen behind Max's two 2026-08-03 requests:
//   * Usage: how many times a day each employee opens the portal and how long they actually use
//     it, so the people who never log in can be found and nudged.
//   * Quote of the Day: review the quotes Claude proposes before they appear to staff, and add or
//     edit them by hand.
// The two tabs are separate permissions (usage_analytics = view, quote_management = edit), so a
// manager can be given the usage report without the power to publish quotes.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Activity, AlarmClock, BadgeCheck, CalendarRange, Check, ChevronDown, ChevronRight, Download,
  Pencil, Plus, Quote as QuoteIcon, RefreshCw, Sparkles, Trash2, TriangleAlert, Users, X,
} from 'lucide-react';
import Header from './Header';
import './style.css';
import './ProjectApproval.css';
import './PortalInsights.css';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import { fmtDateTimeVN, todayVN } from './timeZone';
import i18n from './i18n';
import ModuleHelp from './ModuleHelp';

// Dictionary keys (xInsights.*), translated where they are drawn. The object KEYS are the stored
// audience / status values and are what the code compares.
/* THE GUIDE (Max 01/11: every module has one). Written for the Usage tab first, then the Quotes tab; a step whose control
   is not drawn (the other tab, a tab the person does not get) is skipped by TourGuide. The page shell has no loading
   state of its own — the tabs' own tiles and toolbars are drawn at once, so the guide is ready from the first render. */
const TOUR = [
  { selector: '.pa-tabs.view-tabs', title: 'tourInsights.tabsT', body: 'tourInsights.tabsB' },
  { selector: '.pi-range', title: 'tourInsights.rangeT', body: 'tourInsights.rangeB' },
  { selector: '.pi-tiles', title: 'tourInsights.tilesT', body: 'tourInsights.tilesB' },
  { selector: '[data-pi-usage]', title: 'tourInsights.usageT', body: 'tourInsights.usageB' },
  { selector: '.pi-filters', title: 'tourInsights.quoteFiltersT', body: 'tourInsights.quoteFiltersB' },
  { selector: '.pi-table-quotes', title: 'tourInsights.quoteListT', body: 'tourInsights.quoteListB' },
];

const AUDIENCE_LABEL = { portal: 'xInsights.aud.portal', developer: 'xInsights.aud.developer' };
const STATUS_LABEL = { pending: 'xInsights.status.pending', approved: 'xInsights.status.approved', rejected: 'xInsights.status.rejected' };

/** 95 -> "1h 35m", 40 -> "40m", 0 -> "—" — the units in the reader's language (i18n.t at render). */
const asDuration = (minutes) => {
  const m = Math.round(minutes || 0);
  if (!m) return '—';
  if (m < 60) return i18n.t('xInsights.durMin', { m });
  return i18n.t('xInsights.durHm', { h: Math.floor(m / 60), m: String(m % 60).padStart(2, '0') });
};

/* A sentence whose middle is drawn as markup (a <code> path): the translation is asked with a
   marker in the placeholder and split around it, so each language keeps its own word order. */
const MARK = '\u0001';
const splitAtMark = (text) => {
  const i = text.indexOf(MARK);
  return i === -1 ? [text, ''] : [text.slice(0, i), text.slice(i + MARK.length)];
};

const dateNDaysAgo = (n) => {
  const d = new Date(Date.now() - n * 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Ho_Chi_Minh' }).format(d);
};

const emptyQuoteForm = {
  audience: 'portal', quoteEn: '', quoteKo: '', quoteVi: '', author: '', authorKo: '',
  authorTitleEn: '', authorTitleKo: '', authorTitleVi: '', orgEn: '', orgKo: '', orgVi: '',
  // Simplified Chinese (2026-10-21). Always sent by this form, so a save writes it; an older form that does not
  // send it leaves the stored Chinese alone (the server's has_zh).
  quoteZh: '', authorZh: '', authorTitleZh: '', orgZh: '',
  // French and Japanese (04/11/2026), sent the same way (the server's has_fr / has_ja)
  quoteFr: '', authorFr: '', authorTitleFr: '', orgFr: '', quoteJa: '', authorJa: '', authorTitleJa: '', orgJa: '',
  birthYear: '', deathYear: '', theme: '', attributionNote: '',
};

// ============================================================================ usage tab
const UsageTab = () => {
  const { t } = useTranslation();
  const [from, setFrom] = useState(dateNDaysAgo(29));
  const [to, setTo] = useState(todayVN());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openUser, setOpenUser] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/usage/summary`, { params: { from, to } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || t('xInsights.usage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [from, to, t]);

  useEffect(() => { load(); }, [load]);

  // Memoised so the two useMemo blocks below do not recompute on every render.
  const users = useMemo(() => data?.users || [], [data]);
  const totals = useMemo(() => {
    const used = users.filter((u) => u.visits > 0);
    return {
      accounts: users.length,
      used: used.length,
      unused: users.length - used.length,
      visits: users.reduce((s, u) => s + (u.visits || 0), 0),
      minutes: users.reduce((s, u) => s + (u.activeMinutes || 0), 0),
    };
  }, [users]);

  /* SORT AND FILTER LIKE EXCEL (Max, 27/10: "thêm sort và filter cho bảng"): the portal's own funnel on every column —
     SortTh + useColumnTools, as Project Approval and List Users have it — and a search over name, e-mail and role. A
     column sorts and filters on what its cell shows; a person's day rows stay under them whatever the order. */
  const columns = [
    { key: 'user', get: (u) => u.fullName || u.userName || '' },
    { key: 'role', get: (u) => u.role || '' },
    { key: 'visits', get: (u) => Number(u.visits || 0) },
    { key: 'minutes', get: (u) => Number(u.activeMinutes || 0) },
    { key: 'days', get: (u) => Number(u.activeDays || 0) },
    { key: 'avg', get: (u) => Number(u.avgMinutesPerActiveDay || 0) },
    { key: 'lastSeen', get: (u) => (u.lastSeenUtc ? fmtDateTimeVN(u.lastSeenUtc, '') : t('xInsights.usage.never')) },
  ];
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? users.filter((u) => [u.fullName, u.userName, u.email, u.role].join(' ').toLowerCase().includes(q)) : users;
  }, [users, search]);
  const tools = useColumnTools(searched, columns);

  const dailyByUser = useMemo(() => {
    const map = {};
    for (const row of data?.daily || []) {
      (map[row.userName] = map[row.userName] || []).push(row);
    }
    return map;
  }, [data]);

  const exportCsv = () => {
    // Export only — nothing reads this file back, so the headings follow the reader's language.
    const head = ['csvUser', 'csvName', 'csvEmail', 'csvRole', 'csvVisits', 'csvMinutes', 'csvDays', 'csvAvg', 'csvLastSeen']
      .map((k) => t(`xInsights.usage.${k}`));
    const rows = users.map((u) => [
      u.userName, u.fullName, u.email, u.role, u.visits, u.activeMinutes, u.activeDays,
      u.avgMinutesPerActiveDay, u.lastSeenUtc ? fmtDateTimeVN(u.lastSeenUtc, '') : '',
    ]);
    const csv = [head, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    // BOM so Excel opens the Vietnamese/Korean characters correctly
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `portal-usage_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="pi-panel">
      <div className="pi-toolbar">
        <div className="pi-range">
          <CalendarRange size={16} strokeWidth={2.2} />
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          <span>→</span>
          <input type="date" value={to} min={from} max={todayVN()} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="pi-presets">
          {[[6, 7], [29, 30], [89, 90]].map(([n, label]) => (
            <button key={label} type="button" onClick={() => { setFrom(dateNDaysAgo(n)); setTo(todayVN()); }}>
              {t('xInsights.usage.preset', { n: label })}
            </button>
          ))}
        </div>
        <div className="pi-toolbar-right">
          <button type="button" className="pi-btn" onClick={load} disabled={loading}>
            <RefreshCw size={15} strokeWidth={2.2} /> {t('xInsights.refresh')}
          </button>
          <button type="button" className="pi-btn" onClick={exportCsv} disabled={!users.length}>
            <Download size={15} strokeWidth={2.2} /> CSV
          </button>
        </div>
      </div>

      {error && <div className="pi-error"><TriangleAlert size={16} /> {error}</div>}

      <div className="pi-tiles">
        <div className="pi-tile">
          <span className="pi-tile-label"><Users size={14} /> {t('xInsights.usage.tileAccounts')}</span>
          <span className="pi-tile-value">{totals.accounts}</span>
        </div>
        <div className="pi-tile">
          <span className="pi-tile-label"><BadgeCheck size={14} /> {t('xInsights.usage.tileUsed')}</span>
          <span className="pi-tile-value">{totals.used}</span>
        </div>
        <div className={`pi-tile ${totals.unused ? 'pi-tile-warn' : ''}`}>
          <span className="pi-tile-label"><TriangleAlert size={14} /> {t('xInsights.usage.tileUnused')}</span>
          <span className="pi-tile-value">{totals.unused}</span>
        </div>
        <div className="pi-tile">
          <span className="pi-tile-label"><Activity size={14} /> {t('xInsights.usage.tileVisits')}</span>
          <span className="pi-tile-value">{totals.visits}</span>
        </div>
        <div className="pi-tile">
          <span className="pi-tile-label"><AlarmClock size={14} /> {t('xInsights.usage.tileTime')}</span>
          <span className="pi-tile-value">{asDuration(totals.minutes)}</span>
        </div>
      </div>

      <div className="pa-toolbar pi-listbar">
        <input className="pa-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t('xInsights.usage.search')} data-pi-search />
        <span className="pi-count">{t('xInsights.usage.shown', { n: tools.shown.length, total: users.length })}</span>
        {tools.active && <button type="button" className="pi-btn" onClick={tools.clear}>{t('xInsights.usage.clearSort')}</button>}
      </div>

      {/* the heading stays put while the list scrolls (Max, 27/10: "freeze/stick header luôn nha") */}
      <div className="pa-table-wrap pi-scroll">
        <table className="pa-table pi-table" data-pi-usage>
          <thead>
            <tr>
              <th style={{ width: 26 }} />
              <SortTh label={t('xInsights.usage.colUser')} {...tools.thProps('user')} />
              <SortTh label={t('xInsights.usage.colRole')} {...tools.thProps('role')} />
              <SortTh label={t('xInsights.usage.colVisits')} className="pi-num" {...tools.thProps('visits')} />
              <SortTh label={t('xInsights.usage.colTime')} className="pi-num" {...tools.thProps('minutes')} />
              <SortTh label={t('xInsights.usage.colDays')} className="pi-num" {...tools.thProps('days')} />
              <SortTh label={t('xInsights.usage.colAvg')} className="pi-num" {...tools.thProps('avg')} />
              <SortTh label={t('xInsights.usage.colLastSeen')} {...tools.thProps('lastSeen')} />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="pi-muted">{t('xInsights.loading')}</td></tr>}
            {!loading && !tools.shown.length && <tr><td colSpan={8} className="pi-muted">{t('xInsights.usage.noAccounts')}</td></tr>}
            {!loading && tools.shown.map((u) => {
              const days = dailyByUser[u.userName] || [];
              const open = openUser === u.userName;
              return (
                <React.Fragment key={u.userName}>
                  <tr className={u.visits ? '' : 'pi-row-idle'}>
                    <td>
                      {days.length > 0 && (
                        <button type="button" className="pi-expand" onClick={() => setOpenUser(open ? null : u.userName)}>
                          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        </button>
                      )}
                    </td>
                    <td>
                      <span className="pi-user">{u.fullName || u.userName}</span>
                      <span className="pi-sub">{u.email || u.userName}</span>
                    </td>
                    <td>{u.role || '—'}</td>
                    <td className="pi-num">{u.visits || 0}</td>
                    <td className="pi-num">{asDuration(u.activeMinutes)}</td>
                    <td className="pi-num">{u.activeDays || 0}</td>
                    <td className="pi-num">{asDuration(u.avgMinutesPerActiveDay)}</td>
                    <td>
                      {u.lastSeenUtc
                        ? fmtDateTimeVN(u.lastSeenUtc)
                        : <span className="pi-never">{t('xInsights.usage.never')}</span>}
                    </td>
                  </tr>
                  {open && days.map((d) => (
                    <tr key={`${u.userName}-${d.useDate}`} className="pi-row-day">
                      <td />
                      <td colSpan={2}>{String(d.useDate).slice(0, 10)}</td>
                      <td className="pi-num">{d.visits}</td>
                      <td className="pi-num">{asDuration(d.activeMinutes)}</td>
                      <td className="pi-num">{d.pageViews}</td>
                      <td className="pi-num" />
                      <td>{fmtDateTimeVN(d.firstSeenUtc)} → {fmtDateTimeVN(d.lastSeenUtc)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {(data?.topPages || []).length > 0 && (
        <div className="pi-subsection">
          <h3>{t('xInsights.usage.topPages')}</h3>
          <div className="pi-pages">
            {data.topPages.map((p) => (
              <div className="pi-page-row" key={p.path}>
                <span className="pi-page-path">{p.path}</span>
                <span className="pi-page-count">{p.visits}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="pi-note">{t('xInsights.usage.note')}</p>
    </div>
  );
};

// ============================================================================ quotes tab
const QuoteRow = ({ q, canEdit, onStatus, onEdit, onDelete, busy }) => {
  const { t } = useTranslation();
  return (
  <tr className={`pi-quote-row pi-quote-${q.status}`}>
    <td>
      <span className={`pi-badge pi-badge-${q.status}`}>{STATUS_LABEL[q.status] ? t(STATUS_LABEL[q.status]) : q.status}</span>
      <span className="pi-sub">{q.source === 'ai' ? `AI · ${q.aiModel || t('xInsights.quotes.modelFallback')}` : q.source}</span>
    </td>
    <td>
      <p className="pi-quote-en">{q.quoteEn}</p>
      {q.quoteVi && <p className="pi-quote-ko">{q.quoteVi}</p>}
      {q.quoteKo && <p className="pi-quote-ko">{q.quoteKo}</p>}
      {q.quoteZh && <p className="pi-quote-ko">{q.quoteZh}</p>}
      {q.quoteFr && <p className="pi-quote-ko">{q.quoteFr}</p>}
      {q.quoteJa && <p className="pi-quote-ko">{q.quoteJa}</p>}
      {q.attributionNote && <p className="pi-quote-note">{t('xInsights.quotes.source', { note: q.attributionNote })}</p>}
    </td>
    <td>
      <span className="pi-user">{q.author} {q.lifespan}</span>
      <span className="pi-sub">{[q.authorTitleEn, q.orgEn].filter(Boolean).join(' · ')}</span>
      {q.authorKo && <span className="pi-sub">{q.authorKo}</span>}
    </td>
    <td>{AUDIENCE_LABEL[q.audience] ? t(AUDIENCE_LABEL[q.audience]) : q.audience}</td>
    <td className="pi-num">{q.shownCount}</td>
    <td className="pi-actions">
      {canEdit && q.status !== 'approved' && (
        <button type="button" className="pi-icon pi-ok" title={t('xInsights.quotes.publish')} disabled={busy}
          onClick={() => onStatus(q, 'approved')}><Check size={15} /></button>
      )}
      {canEdit && q.status !== 'rejected' && (
        <button type="button" className="pi-icon pi-no" title={t('xInsights.quotes.reject')} disabled={busy}
          onClick={() => onStatus(q, 'rejected')}><X size={15} /></button>
      )}
      {canEdit && (
        <button type="button" className="pi-icon" title={t('xInsights.quotes.edit')} disabled={busy}
          onClick={() => onEdit(q)}><Pencil size={14} /></button>
      )}
      {canEdit && (
        <button type="button" className="pi-icon pi-no" title={t('xInsights.quotes.delete')} disabled={busy}
          onClick={() => onDelete(q)}><Trash2 size={14} /></button>
      )}
    </td>
  </tr>
  );
};

const QuoteForm = ({ initial, onCancel, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...emptyQuoteForm, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.quoteEn.trim() || !form.author.trim()) {
      setError(t('xInsights.form.required'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (form.quoteId) {
        await axios.put(`${BACKEND_URL}/api/admin/quotes/${form.quoteId}`, form);
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/quotes`, { ...form, status: 'approved' });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || t('xInsights.form.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pi-form">
      <div className="pi-form-head">
        <h3>{form.quoteId ? t('xInsights.form.editTitle', { id: form.quoteId }) : t('xInsights.form.addTitle')}</h3>
        <button type="button" className="pi-icon" onClick={onCancel} title={t('xInsights.form.close')} aria-label={t('xInsights.form.close')}><X size={16} /></button>
      </div>
      {error && <div className="pi-error"><TriangleAlert size={16} /> {error}</div>}
      <div className="pi-form-grid">
        <label className="pi-field pi-span2">
          <span>{t('xInsights.form.quoteEn')}</span>
          <textarea rows={3} value={form.quoteEn} onChange={set('quoteEn')} />
        </label>
        <label className="pi-field pi-span2">
          <span>{t('xInsights.form.quoteVi')}</span>
          <textarea rows={3} value={form.quoteVi} onChange={set('quoteVi')} />
        </label>
        <label className="pi-field pi-span2">
          <span>{t('xInsights.form.quoteKo')}</span>
          <textarea rows={3} value={form.quoteKo} onChange={set('quoteKo')} />
        </label>
        <label className="pi-field pi-span2">
          <span>{t('xInsights.form.quoteZh')}</span>
          <textarea rows={2} value={form.quoteZh} onChange={set('quoteZh')} />
        </label>
        <label className="pi-field pi-span2">
          <span>{t('xInsights.form.quoteFr')}</span>
          <textarea rows={2} value={form.quoteFr} onChange={set('quoteFr')} />
        </label>
        <label className="pi-field pi-span2">
          <span>{t('xInsights.form.quoteJa')}</span>
          <textarea rows={2} value={form.quoteJa} onChange={set('quoteJa')} />
        </label>
        <label className="pi-field"><span>{t('xInsights.form.author')}</span>
          <input value={form.author} onChange={set('author')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.authorKo')}</span>
          <input value={form.authorKo} onChange={set('authorKo')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.authorZh')}</span>
          <input value={form.authorZh} onChange={set('authorZh')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.authorFr')}</span>
          <input value={form.authorFr} onChange={set('authorFr')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.authorJa')}</span>
          <input value={form.authorJa} onChange={set('authorJa')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.roleEn')}</span>
          <input value={form.authorTitleEn} onChange={set('authorTitleEn')} placeholder="Industrial designer" /></label>
        <label className="pi-field"><span>{t('xInsights.form.roleVi')}</span>
          <input value={form.authorTitleVi} onChange={set('authorTitleVi')} placeholder="Nhà thiết kế công nghiệp" /></label>
        <label className="pi-field"><span>{t('xInsights.form.roleKo')}</span>
          <input value={form.authorTitleKo} onChange={set('authorTitleKo')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.roleZh')}</span>
          <input value={form.authorTitleZh} onChange={set('authorTitleZh')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.roleFr')}</span>
          <input value={form.authorTitleFr} onChange={set('authorTitleFr')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.roleJa')}</span>
          <input value={form.authorTitleJa} onChange={set('authorTitleJa')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.org')}</span>
          <input value={form.orgEn} onChange={set('orgEn')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.orgVi')}</span>
          <input value={form.orgVi} onChange={set('orgVi')} placeholder={t('xInsights.form.orgViPh')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.orgKo')}</span>
          <input value={form.orgKo} onChange={set('orgKo')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.orgZh')}</span>
          <input value={form.orgZh} onChange={set('orgZh')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.orgFr')}</span>
          <input value={form.orgFr} onChange={set('orgFr')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.orgJa')}</span>
          <input value={form.orgJa} onChange={set('orgJa')} /></label>
        <label className="pi-field"><span>{t('xInsights.form.birth')}</span>
          <input value={form.birthYear} onChange={set('birthYear')} inputMode="numeric" placeholder="1932" /></label>
        <label className="pi-field"><span>{t('xInsights.form.death')}</span>
          <input value={form.deathYear} onChange={set('deathYear')} inputMode="numeric" /></label>
        <label className="pi-field"><span>{t('xInsights.form.shownOn')}</span>
          <select value={form.audience} onChange={set('audience')}>
            <option value="portal">{t('xInsights.aud.portal')}</option>
            <option value="developer">{t('xInsights.aud.developer')}</option>
          </select></label>
        <label className="pi-field"><span>{t('xInsights.form.theme')}</span>
          <input value={form.theme} onChange={set('theme')} placeholder="quality" /></label>
        <label className="pi-field pi-span2"><span>{t('xInsights.form.source')}</span>
          <input value={form.attributionNote} onChange={set('attributionNote')}
            placeholder={t('xInsights.form.sourcePh')} /></label>
      </div>
      <div className="pi-form-foot">
        <button type="button" className="pi-btn" onClick={onCancel}>{t('xInsights.form.cancel')}</button>
        <button type="button" className="pi-btn pi-btn-primary" onClick={save} disabled={saving}>
          {saving ? t('xInsights.form.saving') : t('xInsights.form.save')}
        </button>
      </div>
    </div>
  );
};

const QuotesTab = ({ canEdit }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [genAudience, setGenAudience] = useState('portal');
  const [genCount, setGenCount] = useState(8);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/quotes`, {
        params: { status: statusFilter || undefined, audience: audienceFilter || undefined },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || t('xInsights.quotes.loadError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, audienceFilter, t]);

  useEffect(() => { load(); }, [load]);

  const countOf = (audience, status) =>
    (data?.counts || []).filter((c) => (!audience || c.audience === audience) && (!status || c.status === status))
      .reduce((s, c) => s + c.n, 0);
  /* One approved quote covers one day, so an audience with fewer approved quotes than the cooldown
     will repeat. Both the warning and the "curated by hand" note read this. */
  const shortAudiences = (d) => ['portal', 'developer']
    .map((aud) => ({ aud, n: countOf(aud, 'approved') }))
    .filter((x) => x.n < (d?.cooldownDays || 0));
  const coverageShort = (d) => shortAudiences(d).length > 0;

  const setStatus = async (q, status) => {
    setBusy(true);
    setNotice('');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/quotes/${q.quoteId}`, { status, statusOnly: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || t('xInsights.quotes.updateError'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (q) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('xInsights.quotes.deleteConfirm', { author: q.author }))) return;
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/quotes/${q.quoteId}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || t('xInsights.quotes.deleteError'));
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/quotes/generate`, {
        audience: genAudience, count: Number(genCount) || 8,
      });
      // Whole sentences, one per fact, so no language has to be stitched from fragments.
      setNotice([
        t('xInsights.quotes.genAdded', { n: res.data.inserted }),
        res.data.skipped ? t('xInsights.quotes.genSkipped', { n: res.data.skipped }) : '',
        res.data.rejected?.length ? t('xInsights.quotes.genDropped', { n: res.data.rejected.length }) : '',
        t('xInsights.quotes.genModel', { model: res.data.model }),
      ].filter(Boolean).join(' '));
      setStatusFilter('pending');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || t('xInsights.quotes.genFailed'));
    } finally {
      setBusy(false);
    }
  };

  const quotes = data?.quotes || [];

  return (
    <div className="pi-panel">
      <div className="pi-ai-box">
        <div className="pi-ai-head">
          <Sparkles size={17} strokeWidth={2.2} />
          <div>
            <strong>{t('xInsights.quotes.aiHead')}</strong>
            <p>{t('xInsights.quotes.aiBody')}</p>
          </div>
        </div>
        {/* One approved quote = one day on screen, so the pool size IS the no-repeat guarantee.
            Jay saw the same quotes come round because the home page had 7 of them (2026-08-24). */}
        {data && (() => {
          const rule = data.cooldownDays || 0;
          const short = shortAudiences(data);
          if (!short.length) return null;
          const label = (aud) => (aud === 'portal' ? t('xInsights.aud.portalShort') : t('xInsights.aud.developerShort'));
          return (
            <div className="pi-warn">
              <TriangleAlert size={16} />
              <span>
                <strong>{t('xInsights.quotes.shortLead', { days: rule })}</strong>{' '}
                {short.map((x) => t('xInsights.quotes.shortItem', { screen: label(x.aud), n: x.n })).join(' · ')}.
                {' '}{t('xInsights.quotes.shortTail')}
              </span>
            </div>
          );
        })()}
        {/* A missing key is only a problem when the hand-curated pool cannot cover the no-repeat
            rule. Max chose to fill the pool by hand instead of enabling the generator (2026-08-24),
            so once coverage is fine this states the situation instead of flagging it. */}
        {data && !data.aiConfigured ? (
          <div className={coverageShort(data) ? 'pi-warn' : 'pi-info'}>
            {coverageShort(data) ? <TriangleAlert size={16} /> : <QuoteIcon size={15} />}
            <span>
              {coverageShort(data) ? (
                <>
                  <strong>{t('xInsights.quotes.noKeyLead')}</strong>{' '}{t('xInsights.quotes.noKeyRest')}
                </>
              ) : (
                <>
                  <strong>{t('xInsights.quotes.curatedLead')}</strong>{' '}
                  {t('xInsights.quotes.curatedRest', { n: countOf('', 'approved'), days: data.cooldownDays })}
                </>
              )}
              {' '}{(() => {
                const [before, after] = splitAtMark(t('xInsights.quotes.addKey', { path: MARK }));
                return <>{before}<code>/app/backend/.env</code>{after}</>;
              })()}
            </span>
          </div>
        ) : (
          <div className="pi-gen">
            <select value={genAudience} onChange={(e) => setGenAudience(e.target.value)}>
              <option value="portal">{t('xInsights.aud.portal')}</option>
              <option value="developer">{t('xInsights.aud.developer')}</option>
            </select>
            <input type="number" min={1} max={20} value={genCount}
              onChange={(e) => setGenCount(e.target.value)} />
            <button type="button" className="pi-btn pi-btn-primary" onClick={generate} disabled={busy || !canEdit}>
              <Sparkles size={15} strokeWidth={2.2} /> {busy ? t('xInsights.quotes.generating') : t('xInsights.quotes.generateNow')}
            </button>
            <span className="pi-sub">{t('xInsights.quotes.modelLabel', { model: data?.aiModel || '' })}</span>
          </div>
        )}
      </div>

      {error && <div className="pi-error"><TriangleAlert size={16} /> {error}</div>}
      {notice && <div className="pi-notice"><Check size={16} /> {notice}</div>}

      <div className="pi-tiles">
        <div className={`pi-tile ${countOf('', 'pending') ? 'pi-tile-warn' : ''}`}>
          <span className="pi-tile-label"><QuoteIcon size={14} /> {t('xInsights.status.pending')}</span>
          <span className="pi-tile-value">{countOf('', 'pending')}</span>
        </div>
        <div className="pi-tile">
          <span className="pi-tile-label"><BadgeCheck size={14} /> {t('xInsights.quotes.publishedHome')}</span>
          <span className="pi-tile-value">{countOf('portal', 'approved')}</span>
        </div>
        <div className="pi-tile">
          <span className="pi-tile-label"><BadgeCheck size={14} /> {t('xInsights.quotes.publishedDev')}</span>
          <span className="pi-tile-value">{countOf('developer', 'approved')}</span>
        </div>
      </div>

      <div className="pi-toolbar">
        <div className="pi-filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('xInsights.status.all')}</option>
            <option value="pending">{t('xInsights.status.pending')}</option>
            <option value="approved">{t('xInsights.status.approved')}</option>
            <option value="rejected">{t('xInsights.status.rejected')}</option>
          </select>
          <select value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)}>
            <option value="">{t('xInsights.aud.both')}</option>
            <option value="portal">{t('xInsights.aud.portalShort')}</option>
            <option value="developer">{t('xInsights.aud.developerShort')}</option>
          </select>
        </div>
        <div className="pi-toolbar-right">
          <button type="button" className="pi-btn" onClick={load} disabled={loading}>
            <RefreshCw size={15} strokeWidth={2.2} /> {t('xInsights.refresh')}
          </button>
          {canEdit && (
            <button type="button" className="pi-btn pi-btn-primary" onClick={() => setEditing({ ...emptyQuoteForm })}>
              <Plus size={15} strokeWidth={2.2} /> {t('xInsights.quotes.add')}
            </button>
          )}
        </div>
      </div>

      {editing && (
        <QuoteForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <div className="pa-table-wrap pi-scroll">
        <table className="pa-table pi-table pi-table-quotes">
          <thead>
            <tr>
              <th style={{ width: 130 }}>{t('xInsights.quotes.colStatus')}</th>
              <th>{t('xInsights.quotes.colQuote')}</th>
              <th style={{ width: 210 }}>{t('xInsights.quotes.colAttribution')}</th>
              <th style={{ width: 150 }}>{t('xInsights.quotes.colShownOn')}</th>
              <th className="pi-num" style={{ width: 60 }}>{t('xInsights.quotes.colTimes')}</th>
              <th style={{ width: 128 }} />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="pi-muted">{t('xInsights.loading')}</td></tr>}
            {!loading && !quotes.length && <tr><td colSpan={6} className="pi-muted">{t('xInsights.quotes.noMatch')}</td></tr>}
            {!loading && quotes.map((q) => (
              <QuoteRow key={q.quoteId} q={q} canEdit={canEdit} busy={busy}
                onStatus={setStatus} onEdit={setEditing} onDelete={remove} />
            ))}
          </tbody>
        </table>
      </div>

      {(data?.history || []).length > 0 && (
        <div className="pi-subsection">
          <h3>{t('xInsights.quotes.recent')}</h3>
          <div className="pi-pages">
            {data.history.map((h) => (
              <div className="pi-page-row" key={`${h.showDate}-${h.audience}`}>
                <span className="pi-page-path">
                  {String(h.showDate).slice(0, 10)} · {h.audience === 'developer' ? t('xInsights.aud.developerShort') : t('xInsights.aud.home')} · {h.author}
                </span>
                <span className="pi-sub pi-page-quote">{h.quoteEn}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================ page shell
const PortalInsights = ({ functions = [] }) => {
  const { t } = useTranslation();
  const can = (key) => Array.isArray(functions) && functions.includes(key);
  const canUsage = can('usage_analytics');
  const canQuotes = can('quote_management');
  const [tab, setTab] = useState(canUsage ? 'usage' : 'quotes');

  return (
    /* THE PORTAL'S OWN PAGE (Max, 27/10: "UI của view này bị khác biệt với các view khác"): the frame, title and tabs
       of Project Approval and the other screens, instead of a centred card of its own with black pill tabs */
    <div className="pa-page">
      <Header />
      <main className="pa-main pi-page">
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('xInsights.title')}</h2>
            <p>{t('xInsights.sub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="portalInsights" onBeforeTour={() => { if (canUsage) setTab('usage'); }} />
        </div>

        <div className="pa-tabs view-tabs" role="tablist">
          {canUsage && (
            <button type="button" className={`pa-tab ${tab === 'usage' ? 'active' : ''}`} onClick={() => setTab('usage')} data-pi-tab="usage">
              <Activity size={15} strokeWidth={2.2} /> {t('xInsights.tabUsage')}
            </button>
          )}
          {canQuotes && (
            <button type="button" className={`pa-tab ${tab === 'quotes' ? 'active' : ''}`} onClick={() => setTab('quotes')} data-pi-tab="quotes">
              <QuoteIcon size={15} strokeWidth={2.2} /> {t('xInsights.tabQuotes')}
            </button>
          )}
        </div>

        {tab === 'usage' && canUsage && <UsageTab />}
        {tab === 'quotes' && canQuotes && <QuotesTab canEdit={canQuotes} />}
      </main>
    </div>
  );
};

export default PortalInsights;
